import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import { EDITING_OPTIONS, ENTITLEMENTS } from '../application-core/constants';
import ActionText from '../core/ActionText';
import AddButton from '../core/AddButton';
import BasicLanguageFieldDisplay from '../core/BasicLanguageFieldDisplay';
import DatatypeDisplay from '../core/DatatypeDisplay';
import Errored from '../core/Errored';
import Notification from '../core/Notification';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { MAX_DESCRIPTION_LENGTH, STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';
import { checkResourceEntitlements, getArrayValues, getGraphFromProject, isInContextComponent, makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import withGS from '../core/withGShoc';
import WorkflowResourceManager from '../core/workflow/WorkflowResourceManager';
import GS from '../grafsync/grafsync';
import { EXPANDED_NOTE_FIELDS, MAX_DEFINITION_LENGTH } from '../vocabulary/constants';
import { getPairwiseValues } from '../vocabulary/utils/utils';
import AdditionalDetail from './AdditionalDetail';
import { MAX_CLASSNAME_LENGTH } from './constants';
import DocumentSource from './DocumentSource';
import FindResourceDialog from './FindResourceDialog';
import ResourceDropRelationshipsDisplay from './ResourceDropRelationshipsDisplay';
import Visibility from './Visibility';

class ClassDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            warning: "",
            isErrored: null
        };
        this.cancellable = null;
    }

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewResource = (property, propertyGraphIRI, suppliedPropertyType) => (iri) => {
        GS.loadData([iri], {}, this.props.graph).then(() => GS.addProperty(this.props.id, this.props.graph, property, iri, null, null, propertyGraphIRI, suppliedPropertyType));
    };

    addDefinedBy = () => GS.addProperty(this.props.id, this.props.graph, "isDefinedBy", this.props.ontologyIRI);

    onUpdate = (property, lang, index, propertyGraphIRI) => (value) => this.props.onUpdate(property, value, lang, index, propertyGraphIRI);

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) => this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    onExitWarning = () => this.setState({ warning: "" });

    doPath(payload) {
        this.cancellable = makeCancelable(GS.path(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    checkClass = async (id) => {
        const { data } = this.props;
        if (this.props.id === id) return "You cannot add relations where a class is related to itself.";
        if (data.disjointWith && data.disjointWith.includes(id)) {
            return "You cannot add a class that is already disjoint.";
        }
        if (data.equivalentClass && data.equivalentClass.includes(id)) {
            return "You cannot add a class that is already equivalent.";
        }
        if (data.subClassOf && data.subClassOf.includes(id)) {
            return "You cannot add a class that is already a subclass of.";
        }
        if (data.hasSubClass && data.hasSubClass.includes(id)) {
            return "You cannot add a class that is already a subclass.";
        }
        const subClassOf = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "http://www.w3.org/2000/01/rdf-schema#subClassOf"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (subClassOf.results) {
            if (subClassOf.results.some(result => result.parent === id)) {
                return "You cannot add a class that is already above in the class hierarchy.";
            }
        }
        const hasSubClass = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "https://graphifi.com/schema/xowl/hasSubClass"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (hasSubClass.results) {
            if (hasSubClass.results.some(result => result.parent === id)) {
                return "You cannot add a class that is already lower in the class hierarchy.";
            }
        }
        return null;
    };

    moveClass = (property) => (id) => {
        if (this.props.data[property] && this.props.data[property].includes(id)) return;
        this.checkClass(id)
            .then(result => {
                if (result === null) this.addNewResource(property)(id);
                else {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ warning: result });
                }
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    addDisjointClass = (iri) => GS.insert(this.props.id, this.props.graph, "disjointUnionOf", iri);

    deleteListItem = (listProp, position, iri) => GS.remove(iri, this.props.graph, listProp, position);

    render() {

        const { data, classes, defaultLang, project, ontologyIRI, buildLink, graph, viewLang, excludeDeprecated,
            availableAnnotationProperty, hiddenProps, id, editingOptions, projectUsers, useWorkflow, entitlements } = this.props;
        const { warning, isErrored } = this.state;

        if (data._notAvailable) return null;
        if (warning !== "") return <Warning message={warning} onExitWarning={this.onExitWarning} />;
        if (isErrored) return <Errored data={isErrored} />;
        if (data.pendingReplacement) return <ActionText>This class has been replaced. Select again to view new data.</ActionText>;
        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_ONTOLOGY)
            || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === STATUS_ACCEPTED || data.status[0] === STATUS_REJECTED));

        const hidden = hiddenProps ?
            hiddenProps.map(prop => prop.substring(36))
            :
            [];
        const visibleNoteFields = EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property));
        const dataTypeList = Array.isArray(data.type) ? data.type : [data.type];
        const av = editingOptions && editingOptions.includes(EDITING_OPTIONS.ADVANCED_VIEW);
        const isDefinedBy = getArrayValues(data, "isDefinedBy");

        return (
            <div>
                <ProjectConsumer>
                    {({ id: projectIRI, projectLang, hasComponent }) => (
                        <form autoComplete="off" className={classes.selection}>
                            {data.pendingDeletion &&
                                <Notification error={true} text="This class is pending deletion." dataTest="pending-deletion" />
                            }
                            {dataTypeList.includes("NamedIndividual") &&
                                <Notification text="This class is also a named individual." dataTest="punning" />
                            }
                            {data.deprecated &&
                                <div>
                                    <Notification text="This class is deprecated." dataTest="deprecated" />
                                    {
                                        excludeDeprecated && data.subClassOf && data.subClassOf.includes("http://www.w3.org/2002/07/owl#Thing") &&
                                        <Notification text="This class is hidden at the top level of the hierarchy." dataTest="deprecated-hidden-note" />
                                    }
                                </div>
                            }
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={!isInContextComponent(data, ontologyIRI) || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="http://www.w3.org/2002/07/owl#Class"
                                            componentIRI={ontologyIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item md={12} lg={6}>
                                    <BasicLanguageFieldDisplay
                                        dataTest="resource-label"
                                        message={"Add a Label"} defaultLang={defaultLang}
                                        title="Label" property="label" values={data.label}
                                        name="Label"
                                        placeholder="Enter label text"
                                        projectLang={projectLang}
                                        graph={graph}
                                        onePerLang={true}
                                        maxLength={MAX_CLASSNAME_LENGTH}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate} isReadOnly={isReadOnly}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <TitledPanel dataTest="isDefinedBy" height="full" title="Is Defined By"
                                        actions={!isReadOnly && isDefinedBy.length === 0 ?
                                            <AddButton onClick={this.addDefinedBy}
                                                dataTest="add-isdefined-by"
                                                title="Make defined by this ontology" />
                                            :
                                            null
                                        }>
                                        {isDefinedBy.map(idb =>
                                            <CommonIriDisplay
                                                id={idb}
                                                key={idb}
                                                components={hasComponent}
                                                projectIRI={project}
                                                handleNotAvailable={true}
                                                property="isDefinedBy"
                                                isReadOnly={true}
                                                compact={true}
                                                graph={graph}
                                                source={id}
                                                wrap={false}
                                                viewLang={viewLang}
                                                defaultLang={defaultLang} />
                                        )}
                                    </TitledPanel>
                                </Grid>
                                {
                                    [
                                        {
                                            rel: 'subClassOf',
                                            title: "Is Subclass Of",
                                            allowExternal: true,
                                            expressions: true,
                                            includeSystemGraph: true,
                                            restrictionProperty: true
                                        },
                                        {
                                            rel: 'hasSubClass',
                                            title: "Subclasses",
                                            includeSystemGraph: false,
                                            allowExternal: false
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        const filteredItemData = itemData.filter(item => item !== "http://www.w3.org/2002/07/owl#Thing");
                                        let ebp = [{
                                            predicate: [
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
                                            ],
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentClass"
                                            ],
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentClass"
                                            ],
                                            reverse: true,
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#disjointWith",
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            pathOperators: ["", "*"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#disjointWith",
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            reverse: true,
                                            pathOperators: ["", "*"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                                                "http://www.w3.org/2002/07/owl#disjointWith"
                                            ],
                                            target: data.id,
                                            reverse: true,
                                            pathOperators: ["*", ""]
                                        }];
                                        if (item.rel === "subClassOf") {
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2000/01/rdf-schema#subClassOf"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        if (item.rel === "hasSubClass") {
                                            ebp.push({
                                                predicate: [
                                                    "https://graphifi.com/schema/xowl/hasSubClass"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                onMove={this.moveClass(item.rel)}
                                                addNewResource={this.addNewResource(item.rel)}
                                                property={item.rel}
                                                restrictionProperty={av && item.restrictionProperty}
                                                expressions={av && item.expressions}
                                                graph={graph}
                                                title={item.title}
                                                view="class"
                                                useWorkflow={useWorkflow}
                                                includeSystemGraph={item.includeSystemGraph}
                                                components={hasComponent}
                                                permitExternal={item.allowExternal}
                                                permittedExternalType={item.allowExternal ? "OwlClass" : null}
                                                excludeByPath={ebp}
                                                searchClass="http://www.w3.org/2002/07/owl#Class"
                                                buildLink={buildLink}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={filteredItemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    }
                                    )
                                }
                                {
                                    [
                                        {
                                            rel: 'equivalentClass',
                                            title: "Equivalent Class",
                                            expressions: true,
                                            restrictionProperty: true
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        const filteredItemData = itemData.filter(item => item !== "http://www.w3.org/2002/07/owl#Thing");
                                        let ebp = [{
                                            predicate: [
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            pathOperators: ["+"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
                                            ],
                                            target: data.id,
                                            pathOperators: ["+"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentClass"
                                            ],
                                            reverse: true,
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#disjointWith",
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            pathOperators: ["", "*"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#disjointWith",
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            reverse: true,
                                            pathOperators: ["", "*"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                                                "http://www.w3.org/2002/07/owl#disjointWith"
                                            ],
                                            target: data.id,
                                            reverse: true,
                                            pathOperators: ["*", ""]
                                        }];
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                onMove={this.moveClass(item.rel)}
                                                addNewResource={this.addNewResource(item.rel)}
                                                property={item.rel}
                                                graph={graph}
                                                title={item.title}
                                                view="class"
                                                useWorkflow={useWorkflow}
                                                components={hasComponent}
                                                restrictionProperty={av && item.restrictionProperty}
                                                expressions={av && item.expressions}
                                                permittedExternalType="OwlClass"
                                                excludeByPath={ebp}
                                                permitExternal={true}
                                                searchClass="http://www.w3.org/2002/07/owl#Class"
                                                buildLink={buildLink}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={filteredItemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    }
                                    )
                                }
                                {
                                    [
                                        {
                                            rel: 'disjointWith',
                                            title: "Disjoint With",
                                            expressions: true,
                                            restrictionProperty: true
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        const filteredItemData = itemData.filter(item => item !== "http://www.w3.org/2002/07/owl#Thing");
                                        const ebp = [{
                                            predicate: [
                                                "https://graphifi.com/schema/xowl/hasSubClass"
                                            ],
                                            target: data.id,
                                            pathOperators: ["+"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subClassOf"
                                            ],
                                            target: data.id,
                                            pathOperators: ["+"]
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentClass"
                                            ],
                                            target: data.id
                                        },
                                        {
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentClass"
                                            ],
                                            reverse: true,
                                            target: data.id
                                        }];
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                onMove={this.moveClass(item.rel)}
                                                addNewResource={this.addNewResource(item.rel)}
                                                property={item.rel}
                                                graph={graph}
                                                title={item.title}
                                                view="class"
                                                useWorkflow={useWorkflow}
                                                includeSystemGraph={true}
                                                restrictionProperty={av && item.restrictionProperty}
                                                expressions={av && item.expressions}
                                                permitExternal={true}
                                                permittedExternalType="OwlClass"
                                                excludeByPath={ebp}
                                                components={hasComponent}
                                                exclude={data.disjointWith || []}
                                                searchClass="http://www.w3.org/2002/07/owl#Class"
                                                buildLink={buildLink}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={filteredItemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    }
                                    )
                                }
                                {av &&
                                    <Grid item md={12} lg={6}>
                                        <TitledPanel dataTest="disjointUnionOf" height="full" title="Disjoint Union Of" actions={
                                            !isReadOnly ?
                                                <FindResourceDialog
                                                    key={viewLang}
                                                    source={id}
                                                    permitExternal={false}
                                                    components={hasComponent}
                                                    useWorkflow={useWorkflow}
                                                    exclude={data.disjointUnionOf ? data.disjointUnionOf : []}
                                                    defaultLang={defaultLang}
                                                    sourceComponent={ontologyIRI}
                                                    permittedExternalType="OwlClass"
                                                    buttonTitle="Add a class"
                                                    title="Add Class"
                                                    searchClass="http://www.w3.org/2002/07/owl#Class"
                                                    project={projectIRI}
                                                    graph={graph}
                                                    viewLang={viewLang}
                                                    onAddResource={this.addDisjointClass} />
                                                :
                                                null
                                        }>
                                            {data.disjointUnionOf && data.disjointUnionOf.length === 1 &&
                                                <div style={{ marginBottom: 12 }}>
                                                    <Notification text="There should be at least two items." dataTest="at-least-two" />
                                                </div>
                                            }
                                            {data.disjointUnionOf && data.disjointUnionOf.map((item, itemIndex) => <CommonIriDisplay
                                                id={item}
                                                key={item}
                                                onDeleteIRI={() => this.deleteListItem("disjointUnionOf", itemIndex, id)}
                                                components={hasComponent}
                                                projectIRI={projectIRI}
                                                handleNotAvailable={true}
                                                property="disjointUnionOf"
                                                isReadOnly={isReadOnly}
                                                compact={true}
                                                graph={graph}
                                                source={id}
                                                wrap={false}
                                                viewLang={viewLang}
                                                defaultLang={defaultLang} />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#comment") &&
                                    <Grid item md={12} lg={6}>
                                        <BasicLanguageFieldDisplay
                                            dataTest="comments"
                                            message={"Add a Comment"}
                                            defaultLang={viewLang || defaultLang}
                                            title="Comments"
                                            graph={graph}
                                            placeholder="Enter comment text"
                                            property="comment"
                                            values={data.comment}
                                            projectLang={projectLang}
                                            multiline={true}
                                            maxLength={MAX_DESCRIPTION_LENGTH}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            isReadOnly={isReadOnly}
                                            onAddLanguage={this.addNewLiteral} />
                                    </Grid>
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#seeAlso") &&
                                    [
                                        {
                                            rel: 'seeAlso',
                                            title: "See Also"
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <DatatypeDisplay
                                                title={item.title}
                                                source={data.id}
                                                datatype={""}
                                                components={hasComponent}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                useWorkflow={useWorkflow}
                                                projectLang={projectLang}
                                                property={item.rel}
                                                values={itemData}
                                                onDelete={this.onDelete}
                                                context={ontologyIRI}
                                                onUpdate={this.onUpdate}
                                                projectIRI={projectIRI}
                                                noRangeOptions={{
                                                    language: false,
                                                    dateTime: false,
                                                    datatype: true,
                                                    boolean: false,
                                                    excludeDatatypes: ["INTEGER", "NNINTEGER", "DECIMAL", "LONG", "FLOAT", "DATE", "DOUBLE"]
                                                }}
                                                graph={graph}
                                                associatedProjectGraph={graph}
                                                isReadOnly={isReadOnly}
                                                canAddValues={true}
                                                addNewResource={this.addNewResource(item.rel, graph, "@id")}
                                                onAddLiteral={this.addNewLiteral(item.rel)}
                                                onAddTypedLiteral={this.addNewTypedLiteral(item.rel)}
                                            />
                                        </Grid>;
                                    })
                                }
                                <Grid item md={12} lg={6}>
                                    <Visibility
                                        graph={graph}
                                        viewLang={viewLang}
                                        componentIRI={ontologyIRI}
                                        source={id}
                                        sourceType={data.type}
                                        defaultLang={defaultLang}
                                        sources={data.visibility} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <DocumentSource
                                        dataTest="document-source-list"
                                        graph={graph}
                                        componentIRI={ontologyIRI}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang}
                                        sources={data.documentSource} />
                                </Grid>
                            </Grid>
                            {visibleNoteFields.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>SKOS Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        {EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property)).map(field => {
                                            let existingValues = undefined;
                                            if (field.pairwise) {
                                                existingValues = getPairwiseValues(data);
                                            }
                                            return <Grid key={field.title} item md={12} lg={6}>
                                                <BasicLanguageFieldDisplay
                                                    dataTest={field.property}
                                                    message={"Add " + field.message}
                                                    defaultLang={viewLang || defaultLang}
                                                    title={field.title}
                                                    required={false}
                                                    onePerLang={field.onePerLang === true}
                                                    property={field.property}
                                                    values={data[field.property]}
                                                    existingValues={existingValues}
                                                    maxLength={field.maxLength || MAX_DEFINITION_LENGTH}
                                                    projectLang={projectLang}
                                                    placeholder={field.placeholder}
                                                    name={field.message}
                                                    onDelete={this.onDelete}
                                                    onUpdate={this.onUpdate}
                                                    isReadOnly={isReadOnly}
                                                    multiline={field.multiline}
                                                    onAddLanguage={this.addNewLiteral} />
                                            </Grid>;
                                        }
                                        )}
                                    </Grid>
                                </React.Fragment>
                            }
                            {availableAnnotationProperty && availableAnnotationProperty.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>Additional Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        <AdditionalDetail
                                            data={data}
                                            projectIRI={project}
                                            viewLang={viewLang}
                                            defaultLang={defaultLang}
                                            projectLang={projectLang}
                                            graph={graph}
                                            useWorkflow={useWorkflow}
                                            ontologyIRI={ontologyIRI}
                                            isReadOnly={isReadOnly}
                                            onDelete={this.onDelete}
                                            context={ontologyIRI}
                                            onUpdate={this.onUpdate}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewResource={this.addNewResource}
                                            availableAnnotationProperty={availableAnnotationProperty} />
                                    </Grid>
                                </React.Fragment>
                            }
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div>
        );
    }
}

ClassDetail.defaultProps = {
    hiddenProps: [],
    useWorkflow: false
};

ClassDetail.propTypes = {
    classes: PropTypes.object,
    hiddenProps: PropTypes.array,
    editingOptions: PropTypes.array,
    projectUsers: PropTypes.array,
    id: PropTypes.string.isRequired,
    entitlements: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    excludeDeprecated: PropTypes.bool,
    useWorkflow: PropTypes.bool,
    graph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    viewLang: PropTypes.string,
    buildLink: PropTypes.func.isRequired,
    project: PropTypes.string.isRequired,
    availableAnnotationProperty: PropTypes.array,
    ontologyIRI: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
};

export default withStyles(styles)(withGS(ClassDetail, ["ALL"], null, {}, true));